/**
 * Icons for the onboarding kit.
 *
 * The design uses the Phosphor icon font (`<i class="ph ph-…">`). The shell
 * ships lucide-react, so every Phosphor name used by the onboarding design is
 * mapped to its nearest lucide equivalent here. Import icons from this file
 * (e.g. `ObIcons["seal-check"]`) or straight from lucide-react — the mapping
 * exists so the screens can be ported from the design with one lookup.
 *
 *   Phosphor               → lucide
 *   address-book           → BookUser
 *   arrow-bend-up-right    → CornerUpRight
 *   arrow-clockwise        → RotateCw
 *   arrow-left             → ArrowLeft
 *   arrow-right            → ArrowRight
 *   arrow-up-right         → ArrowUpRight
 *   arrows-left-right      → ArrowLeftRight
 *   buildings              → Building2
 *   calendar-blank         → Calendar
 *   chats-circle           → MessagesSquare
 *   check                  → Check
 *   check-circle           → CircleCheck
 *   check-square           → SquareCheck
 *   circle                 → Circle
 *   circle-notch           → LoaderCircle
 *   clock                  → Clock
 *   cloud-check            → CloudCheck
 *   copy                   → Copy
 *   device-mobile          → Smartphone
 *   download-simple        → Download
 *   envelope-open          → MailOpen
 *   envelope-simple        → Mail
 *   eye / eye-slash        → Eye / EyeOff
 *   file-csv               → FileSpreadsheet
 *   file-text              → FileText
 *   floppy-disk            → Save
 *   folder-simple          → Folder
 *   globe-hemisphere-east  → Globe
 *   hand-waving            → Hand
 *   headset                → Headset
 *   hourglass-medium       → Hourglass
 *   house-line             → House
 *   info                   → Info
 *   lifebuoy               → LifeBuoy
 *   link-break             → Unlink
 *   lock-simple            → Lock
 *   magnifying-glass       → Search
 *   paper-plane-tilt       → Send
 *   password               → KeyRound
 *   pen-nib                → PenTool
 *   pencil-simple          → Pencil
 *   plus                   → Plus
 *   presentation           → Presentation
 *   printer                → Printer
 *   qr-code                → QrCode
 *   rocket-launch          → Rocket
 *   seal-check             → BadgeCheck
 *   share-network          → Share2
 *   shield-check           → ShieldCheck
 *   sparkle                → Sparkles
 *   squares-four           → LayoutGrid
 *   table                  → Table
 *   tag                    → Tag
 *   timer                  → Timer
 *   translate              → Languages
 *   trash                  → Trash2
 *   upload-simple          → Upload
 *   user                   → User
 *   user-gear              → UserCog
 *   user-plus              → UserPlus
 *   users-three            → Users
 *   video-camera           → Video
 *   warning                → TriangleAlert
 *   warning-circle         → CircleAlert
 *   x                      → X
 *
 * Phosphor weights: `bold` ≈ lucide strokeWidth 2.5, `regular` ≈ 2.
 * Phosphor's `fill` weight has no lucide counterpart; the kit uses the
 * outline glyph in the same colour.
 */
import React from "react";
import {
  ArrowLeft,
  ArrowLeftRight,
  ArrowRight,
  ArrowUpRight,
  BadgeCheck,
  BookUser,
  Building2,
  Calendar,
  Check,
  Circle,
  CircleAlert,
  CircleCheck,
  Clock,
  CloudCheck,
  Copy,
  CornerUpRight,
  Download,
  Eye,
  EyeOff,
  FileSpreadsheet,
  FileText,
  Folder,
  Globe,
  Hand,
  Headset,
  Hourglass,
  House,
  Info,
  KeyRound,
  Languages,
  LayoutGrid,
  LifeBuoy,
  LoaderCircle,
  Lock,
  Mail,
  MailOpen,
  MessagesSquare,
  Pencil,
  PenTool,
  Plus,
  Presentation,
  Printer,
  QrCode,
  Rocket,
  RotateCw,
  Save,
  Search,
  Send,
  Share2,
  ShieldCheck,
  Smartphone,
  Sparkles,
  SquareCheck,
  Table,
  Tag,
  Timer,
  Trash2,
  TriangleAlert,
  Unlink,
  Upload,
  User,
  UserCog,
  UserPlus,
  Users,
  Video,
  X,
  type LucideIcon,
} from "lucide-react";

export type { LucideIcon };

export const ObIcons = {
  "address-book": BookUser,
  "arrow-bend-up-right": CornerUpRight,
  "arrow-clockwise": RotateCw,
  "arrow-left": ArrowLeft,
  "arrow-right": ArrowRight,
  "arrow-up-right": ArrowUpRight,
  "arrows-left-right": ArrowLeftRight,
  buildings: Building2,
  "calendar-blank": Calendar,
  "chats-circle": MessagesSquare,
  check: Check,
  "check-circle": CircleCheck,
  "check-square": SquareCheck,
  circle: Circle,
  "circle-notch": LoaderCircle,
  clock: Clock,
  "cloud-check": CloudCheck,
  copy: Copy,
  "device-mobile": Smartphone,
  "download-simple": Download,
  "envelope-open": MailOpen,
  "envelope-simple": Mail,
  eye: Eye,
  "eye-slash": EyeOff,
  "file-csv": FileSpreadsheet,
  "file-text": FileText,
  "floppy-disk": Save,
  "folder-simple": Folder,
  "globe-hemisphere-east": Globe,
  "hand-waving": Hand,
  headset: Headset,
  "hourglass-medium": Hourglass,
  "house-line": House,
  info: Info,
  lifebuoy: LifeBuoy,
  "link-break": Unlink,
  "lock-simple": Lock,
  "magnifying-glass": Search,
  "paper-plane-tilt": Send,
  password: KeyRound,
  "pen-nib": PenTool,
  "pencil-simple": Pencil,
  plus: Plus,
  presentation: Presentation,
  printer: Printer,
  "qr-code": QrCode,
  "rocket-launch": Rocket,
  "seal-check": BadgeCheck,
  "share-network": Share2,
  "shield-check": ShieldCheck,
  sparkle: Sparkles,
  "squares-four": LayoutGrid,
  table: Table,
  tag: Tag,
  timer: Timer,
  translate: Languages,
  trash: Trash2,
  "upload-simple": Upload,
  user: User,
  "user-gear": UserCog,
  "user-plus": UserPlus,
  "users-three": Users,
  "video-camera": Video,
  warning: TriangleAlert,
  "warning-circle": CircleAlert,
  x: X,
} as const satisfies Record<string, LucideIcon>;

export type ObIconName = keyof typeof ObIcons;

export interface IcProps {
  /** A lucide icon component (see the Phosphor mapping above). */
  icon: LucideIcon;
  /** Pixel size; defaults to 1em so the icon follows the text, like the design's icon font. */
  size?: number | string;
  /** Phosphor-style weight. */
  weight?: "regular" | "bold";
  color?: string;
  /** Mirror in RTL (the design's `flip`). */
  flip?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

/** Decorative icon — the design's `<I n=… />`. Always aria-hidden. */
export function Ic({ icon: Icon, size = "1em", weight = "regular", color, flip, className = "", style }: IcProps) {
  return (
    <Icon
      aria-hidden="true"
      focusable="false"
      size={size}
      strokeWidth={weight === "bold" ? 2.5 : 2}
      color={color ?? "currentColor"}
      className={`ob-i${flip ? " ob-flip" : ""}${className ? ` ${className}` : ""}`}
      style={style}
    />
  );
}
